import { Breakpoint, Checkbox, FormGroup } from "@mui/material";
import xor from "lodash/xor";
import { ChangeEvent, Key, ReactElement, useCallback, useMemo } from "react";
import { Option } from "../../../types/option";
import { FormControlLabel } from "../FormControl/FormControl";
import { FormField, FormFieldProps } from "../FormField/FormField";

export interface CheckboxGroupProps<T = unknown> extends FormFieldProps {
  id?: string;
  name: string;
  value: Key[];
  options: Option<string, T>[];
  size?: "small" | "medium";
  onChange: (value: Key[], event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  row?: boolean;
  columns?: number | Partial<Record<Breakpoint, number>>;
}

const toTemplateColumns = (columns?: number) => (columns ? `repeat(${columns}, 1fr)` : undefined);

export const CheckboxGroup = <T,>({
  id,
  name,
  value,
  options,
  size,
  onChange,
  onBlur,
  readOnly,
  row,
  columns,
  ...formFieldProps
}: CheckboxGroupProps<T>): ReactElement => {
  const handleChange = useCallback(
    (option: Option<string, T>) => (event: ChangeEvent<HTMLInputElement>) =>
      onChange(xor(value || [], [option.value]), event),
    [value, onChange],
  );

  const handleBlur = !readOnly ? onBlur : undefined;

  const isChecked = useCallback(
    (option: Option<string, T>) => !!value?.find((v) => v === option.value),
    [value],
  );

  const gridTemplateColumns = useMemo(() => {
    if (!columns) {
      return;
    }

    return typeof columns !== "number"
      ? Object.fromEntries(Object.entries(columns).map(([k, v]) => [k, toTemplateColumns(v)]))
      : toTemplateColumns(columns);
  }, [columns]);

  return (
    <FormField {...formFieldProps} name={name} id={id}>
      <FormGroup
        row={row}
        onBlur={handleBlur}
        sx={!row ? { display: "grid", columnGap: 4, gridTemplateColumns } : undefined}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            label={option.label}
            data-testid={`${name}-option-${option.value}`}
            size={size}
            checked={isChecked(option)}
            sx={readOnly ? { cursor: "default" } : undefined}
            control={
              <Checkbox
                name={name}
                readOnly={readOnly}
                disableRipple={readOnly}
                value={option.value}
                checked={isChecked(option)}
                onChange={!readOnly ? handleChange(option) : undefined}
              />
            }
          />
        ))}
      </FormGroup>
    </FormField>
  );
};
